import { createSlice, createAsyncThunk, nanoid } from "@reduxjs/toolkit";

const API_BASE = process.env.REACT_APP_ELASTIC_API_URL || "";

// Module-level holder for the active EventSource so we can cancel streams
let activeES = null;

// Utility: start a new assistant message and return its id
const createAssistantMsg = () => ({
  id: nanoid(),
  role: "assistant",
  text: "",
});

// Thunk: open SSE stream and append tokens to the latest assistant bubble
export const openChatStream = createAsyncThunk(
  "chat/openStream",
  // 👇 now receives the FULL conversation array
  async (messagesForServer, { dispatch, getState, rejectWithValue }) => {
    try {
      // Close any previous stream
      try { activeES?.close?.(); } catch {}

      // 👇 NEW: encode the messages array into the query string
      const encoded = encodeURIComponent(JSON.stringify(messagesForServer));
      const url = `${API_BASE}/api/assistant/stream?messages=${encoded}`;

      const es = new EventSource(url);
      activeES = es;

      es.onmessage = (evt) => {
        if (evt.data === "[DONE]") {
          dispatch(chatSlice.actions.finishSending());
          es.close();
          return;
        }
        try {
          const payload = JSON.parse(evt.data);
          if (payload.delta)
            dispatch(chatSlice.actions.appendToCurrentAssistant(payload.delta));
          if (payload.error) {
            dispatch(
              chatSlice.actions.appendToCurrentAssistant(
                "\n\n(Something went wrong. Please try again.)"
              )
            );
            dispatch(chatSlice.actions.finishSending());
            es.close();
          }
          if (payload.done) {
            dispatch(chatSlice.actions.finishSending());
            es.close();
          }
        } catch {
          // ignore keepalive/whitespace
        }
      };

      es.onerror = () => {
        dispatch(
          chatSlice.actions.appendToCurrentAssistant(
            "\n\n(Network error. Please try again.)"
          )
        );
        dispatch(chatSlice.actions.finishSending());
        try {
          es.close();
        } catch {}
      };

      return true;
    } catch (e) {
      return rejectWithValue("stream_failed");
    }
  }
);


// Optional non-streaming thunk (POST JSON)
export const sendOnce = createAsyncThunk(
  "chat/sendOnce",
  async (text, { dispatch, rejectWithValue }) => {
    try {
      const r = await fetch(`${API_BASE}/api/assistant/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
      });
      if (!r.ok) throw new Error("assistant_failed");
      const data = await r.json(); // { output }
      dispatch(chatSlice.actions.appendToCurrentAssistant(data.output || "(no response)"));
      dispatch(chatSlice.actions.finishSending());
      return true;
    } catch (e) {
      dispatch(chatSlice.actions.appendToCurrentAssistant("(error)"));
      dispatch(chatSlice.actions.finishSending());
      return rejectWithValue("send_failed");
    }
  }
);

const chatSlice = createSlice({
  name: "chat",
  initialState: {
    messages: [
      {
        id: "sys1",
        role: "assistant",
        text:
          "How can I help you today?",
      },
    ],
    sending: false,
    currentAssistantId: null,
  },
  reducers: {
    addUserMessage: (state, action) => {
      state.messages.push({ id: nanoid(), role: "user", text: action.payload });
    },
    startAssistantMessage: (state) => {
      const msg = createAssistantMsg();
      state.messages.push(msg);
      state.currentAssistantId = msg.id;
    },
    appendToCurrentAssistant: (state, action) => {
      const id = state.currentAssistantId;
      if (!id) return;
      const idx = state.messages.findIndex((m) => m.id === id);
      if (idx >= 0) state.messages[idx].text += action.payload;
    },
    beginSending: (state) => {
      state.sending = true;
    },
    finishSending: (state) => {
      state.sending = false;
      state.currentAssistantId = null;
    },
    cancelStream: (state) => {
      try { activeES?.close?.(); } catch {}
      state.sending = false;
      state.currentAssistantId = null;
    },
    resetChat: (state) => {
      try { activeES?.close?.(); } catch {}
      state.messages = state.initialState?.messages || [
        {
          id: "sys1",
          role: "assistant",
          text:
            "Hey! I can help plan meals, filter by diet or allergens, and build a weekly plan. What are you aiming for this week?",
        },
      ];
      state.sending = false;
      state.currentAssistantId = null;
    },
  },
});

export const {
  addUserMessage,
  startAssistantMessage,
  appendToCurrentAssistant,
  beginSending,
  finishSending,
  cancelStream,
  resetChat,
} = chatSlice.actions;

export default chatSlice.reducer;
